import dayjs from 'dayjs';

/**
 * Recurrence rules for the `/repeat` slash command.
 *
 * Everything here is pure and anchor-explicit so it can be unit tested without
 * a workspace. `parseRepeatRule` takes the anchor because relative bounds
 * (`until friday`) can only be resolved against a starting point; the rule it
 * returns is fully absolute, so `formatRepeatRule` round-trips back through
 * `parseRepeatRule` unchanged.
 */

export const MAX_REPEAT_OCCURRENCES = 180;

/** Occurrences above this need an explicit confirmation before being written. */
export const REPEAT_CONFIRM_THRESHOLD = 30;

export const JOURNAL_DATE_FORMAT = 'YYYY-MM-DD';

export type RepeatUnit = 'days' | 'weeks' | 'months' | 'years';

export type RepeatInterval =
  | { kind: 'step'; unit: RepeatUnit; every: number }
  /** Weekly on a fixed set of weekdays. `days` holds 0=Sunday .. 6=Saturday. */
  | { kind: 'weekdays'; days: number[] };

export type RepeatBound =
  | { kind: 'duration'; unit: RepeatUnit; amount: number }
  | { kind: 'times'; count: number }
  | { kind: 'until'; date: string };

export type RepeatRule = {
  interval: RepeatInterval;
  bound: RepeatBound;
};

const WEEKDAY_NAMES: Record<string, number> = {
  sunday: 0,
  sun: 0,
  su: 0,
  monday: 1,
  mon: 1,
  mo: 1,
  tuesday: 2,
  tues: 2,
  tue: 2,
  tu: 2,
  wednesday: 3,
  weds: 3,
  wed: 3,
  we: 3,
  thursday: 4,
  thurs: 4,
  thur: 4,
  thu: 4,
  th: 4,
  friday: 5,
  fri: 5,
  fr: 5,
  saturday: 6,
  sat: 6,
  sa: 6,
};

const MONTH_NAMES: Record<string, number> = {
  january: 0,
  jan: 0,
  february: 1,
  feb: 1,
  march: 2,
  mar: 2,
  april: 3,
  apr: 3,
  may: 4,
  june: 5,
  jun: 5,
  july: 6,
  jul: 6,
  august: 7,
  aug: 7,
  september: 8,
  sept: 8,
  sep: 8,
  october: 9,
  oct: 9,
  november: 10,
  nov: 10,
  december: 11,
  dec: 11,
};

const UNIT_NAMES: Record<string, RepeatUnit> = {
  day: 'days',
  days: 'days',
  d: 'days',
  week: 'weeks',
  weeks: 'weeks',
  wk: 'weeks',
  wks: 'weeks',
  w: 'weeks',
  month: 'months',
  months: 'months',
  mo: 'months',
  mos: 'months',
  year: 'years',
  years: 'years',
  yr: 'years',
  yrs: 'years',
  y: 'years',
};

/** `every 3 days` and `every 3 day` are the same; `mon,wed` splits on commas. */
function tokenize(input: string): string[] {
  return input
    .toLowerCase()
    .replace(/[,/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(token => token.length > 0 && token !== 'and' && token !== 'the');
}

function isInteger(token: string) {
  return /^\d+$/.test(token);
}

/**
 * Weekday order for display: Monday first, Sunday last, matching how people
 * read a week rather than the 0=Sunday storage order.
 */
function weekdayDisplayOrder(day: number) {
  return day === 0 ? 7 : day;
}

const SHORT_WEEKDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

type Cursor = { tokens: string[]; index: number };

function peek(cursor: Cursor, offset = 0): string | undefined {
  return cursor.tokens[cursor.index + offset];
}

function take(cursor: Cursor): string | undefined {
  return cursor.tokens[cursor.index++];
}

/**
 * Consumes the interval portion from the front of the cursor. Returns
 * undefined without consuming anything if the head does not look like an
 * interval.
 */
function parseInterval(cursor: Cursor): RepeatInterval | undefined {
  const start = cursor.index;

  // `every` / `each` are filler.
  while (peek(cursor) === 'every' || peek(cursor) === 'each') take(cursor);

  const head = peek(cursor);
  if (head === undefined) {
    cursor.index = start;
    return undefined;
  }

  // Single-word shorthands.
  const shorthand: Record<string, RepeatInterval> = {
    daily: { kind: 'step', unit: 'days', every: 1 },
    everyday: { kind: 'step', unit: 'days', every: 1 },
    weekly: { kind: 'step', unit: 'weeks', every: 1 },
    biweekly: { kind: 'step', unit: 'weeks', every: 2 },
    fortnightly: { kind: 'step', unit: 'weeks', every: 2 },
    monthly: { kind: 'step', unit: 'months', every: 1 },
    bimonthly: { kind: 'step', unit: 'months', every: 2 },
    quarterly: { kind: 'step', unit: 'months', every: 3 },
    yearly: { kind: 'step', unit: 'years', every: 1 },
    annually: { kind: 'step', unit: 'years', every: 1 },
  };
  if (shorthand[head]) {
    take(cursor);
    return shorthand[head];
  }

  // `every weekday` / `every weekend`.
  if (head === 'weekday' || head === 'weekdays' || head === 'workday' || head === 'workdays') {
    take(cursor);
    return { kind: 'weekdays', days: [1, 2, 3, 4, 5] };
  }
  if (head === 'weekend' || head === 'weekends') {
    take(cursor);
    return { kind: 'weekdays', days: [6, 0] };
  }

  // `every other day` / `every other week`.
  if (head === 'other') {
    const unit = UNIT_NAMES[peek(cursor, 1) ?? ''];
    if (unit) {
      take(cursor);
      take(cursor);
      return { kind: 'step', unit, every: 2 };
    }
  }

  // A run of weekday names: `mon wed fri`, `monday`, `tue thu`.
  if (WEEKDAY_NAMES[head] !== undefined) {
    const days = new Set<number>();
    while (true) {
      const token = peek(cursor);
      if (token === undefined || WEEKDAY_NAMES[token] === undefined) break;
      days.add(WEEKDAY_NAMES[token]);
      take(cursor);
    }
    return {
      kind: 'weekdays',
      days: [...days].sort((a, b) => weekdayDisplayOrder(a) - weekdayDisplayOrder(b)),
    };
  }

  // `3 days`, `2 weeks`, or a bare `day` / `week` meaning every 1.
  let every = 1;
  if (isInteger(head)) {
    every = parseInt(head, 10);
    take(cursor);
  }
  const unitToken = peek(cursor);
  const unit = unitToken ? UNIT_NAMES[unitToken] : undefined;
  if (!unit || every <= 0) {
    cursor.index = start;
    return undefined;
  }
  take(cursor);
  return { kind: 'step', unit, every };
}

/**
 * Resolves the date after `until`. Accepts an ISO date, a weekday name, a
 * month/day pair in either order, or `end of month` / `end of year`.
 */
function parseUntilDate(cursor: Cursor, anchor: dayjs.Dayjs): string | undefined {
  const head = peek(cursor);
  if (head === undefined) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
    const parsed = dayjs(head, JOURNAL_DATE_FORMAT);
    if (!parsed.isValid()) return undefined;
    take(cursor);
    return parsed.format(JOURNAL_DATE_FORMAT);
  }

  if (head === 'end') {
    const what = peek(cursor, 1) === 'of' ? peek(cursor, 2) : peek(cursor, 1);
    const unit = what ? UNIT_NAMES[what] : undefined;
    if (unit === 'months' || unit === 'years') {
      take(cursor);
      if (peek(cursor) === 'of') take(cursor);
      take(cursor);
      return anchor.endOf(unit === 'months' ? 'month' : 'year').format(JOURNAL_DATE_FORMAT);
    }
    return undefined;
  }

  if (head === 'tomorrow') {
    take(cursor);
    return anchor.add(1, 'day').format(JOURNAL_DATE_FORMAT);
  }

  if (WEEKDAY_NAMES[head] !== undefined) {
    take(cursor);
    const target = WEEKDAY_NAMES[head];
    const diff = (target - anchor.day() + 7) % 7 || 7;
    return anchor.add(diff, 'day').format(JOURNAL_DATE_FORMAT);
  }

  // `dec 31` or `31 dec`.
  const second = peek(cursor, 1);
  let month: number | undefined;
  let day: number | undefined;
  if (MONTH_NAMES[head] !== undefined && second && isInteger(second)) {
    month = MONTH_NAMES[head];
    day = parseInt(second, 10);
  } else if (isInteger(head) && second && MONTH_NAMES[second] !== undefined) {
    day = parseInt(head, 10);
    month = MONTH_NAMES[second];
  }
  if (month === undefined || day === undefined || day < 1 || day > 31) return undefined;
  take(cursor);
  take(cursor);

  let candidate = anchor.month(month).date(1);
  const lastDay = candidate.endOf('month').date();
  candidate = candidate.date(Math.min(day, lastDay));
  if (candidate.isBefore(anchor, 'day')) {
    candidate = candidate.add(1, 'year');
  }
  return candidate.format(JOURNAL_DATE_FORMAT);
}

function parseBound(cursor: Cursor, anchor: dayjs.Dayjs): RepeatBound | undefined {
  if (peek(cursor) === undefined) {
    // No bound given — a month of occurrences is a sane default.
    return { kind: 'duration', unit: 'months', amount: 1 };
  }

  let sawFiller = false;
  while (peek(cursor) === 'for' || peek(cursor) === 'over' || peek(cursor) === 'during') {
    take(cursor);
    sawFiller = true;
  }

  const head = peek(cursor);
  if (head === undefined) {
    // `every 3 days for` is half-typed, not a request for the default.
    return sawFiller ? undefined : { kind: 'duration', unit: 'months', amount: 1 };
  }

  if (head === 'until' || head === 'till' || head === 'til' || head === 'through') {
    take(cursor);
    const date = parseUntilDate(cursor, anchor);
    if (!date) return undefined;
    return { kind: 'until', date };
  }

  // `x5` / `5x`
  const compact = /^x(\d+)$/.exec(head) ?? /^(\d+)x$/.exec(head);
  if (compact) {
    take(cursor);
    const count = parseInt(compact[1], 10);
    return count > 0 ? { kind: 'times', count } : undefined;
  }

  if (isInteger(head)) {
    const amount = parseInt(head, 10);
    if (amount <= 0) return undefined;
    const next = peek(cursor, 1);
    if (next === 'times' || next === 'time' || next === 'occurrences' || next === 'entries') {
      take(cursor);
      take(cursor);
      return { kind: 'times', count: amount };
    }
    const unit = next ? UNIT_NAMES[next] : undefined;
    if (unit) {
      take(cursor);
      take(cursor);
      return { kind: 'duration', unit, amount };
    }
    return undefined;
  }

  // `times 5`
  if (head === 'times' || head === 'time') {
    const next = peek(cursor, 1);
    if (next && isInteger(next)) {
      take(cursor);
      take(cursor);
      const count = parseInt(next, 10);
      return count > 0 ? { kind: 'times', count } : undefined;
    }
    return undefined;
  }

  // A bare unit: `for a month`, `for month`.
  if (head === 'a' || head === 'an' || head === 'one') {
    const next = peek(cursor, 1);
    const unit = next ? UNIT_NAMES[next] : undefined;
    if (unit) {
      take(cursor);
      take(cursor);
      return { kind: 'duration', unit, amount: 1 };
    }
    return undefined;
  }
  const bareUnit = UNIT_NAMES[head];
  if (bareUnit) {
    take(cursor);
    return { kind: 'duration', unit: bareUnit, amount: 1 };
  }

  return undefined;
}

/**
 * Parses a `/repeat` argument. Returns null when the input is not a complete,
 * unambiguous rule — callers show presets in that case rather than guessing.
 */
export function parseRepeatRule(input: string, anchor: string): RepeatRule | null {
  const tokens = tokenize(input);
  if (tokens.length === 0) return null;

  const anchorDay = dayjs(anchor, JOURNAL_DATE_FORMAT);
  if (!anchorDay.isValid()) return null;

  const cursor: Cursor = { tokens, index: 0 };
  const interval = parseInterval(cursor);
  if (!interval) return null;
  if (interval.kind === 'weekdays' && interval.days.length === 0) return null;

  const bound = parseBound(cursor, anchorDay);
  if (!bound) return null;

  // Trailing junk means we did not understand the whole input; better to show
  // presets than to silently ignore what the user typed.
  if (peek(cursor) !== undefined) return null;

  return { interval, bound };
}

function pluralize(amount: number, unit: RepeatUnit) {
  const singular = unit.slice(0, -1);
  return amount === 1 ? singular : unit;
}

/** Canonical, lowercase, re-parseable form. Stored in the block's repeat prop. */
export function formatRepeatRule(rule: RepeatRule): string {
  const { interval, bound } = rule;

  let intervalText: string;
  if (interval.kind === 'step') {
    intervalText =
      interval.every === 1
        ? `every ${pluralize(1, interval.unit)}`
        : `every ${interval.every} ${interval.unit}`;
  } else {
    const days = [...interval.days].sort(
      (a, b) => weekdayDisplayOrder(a) - weekdayDisplayOrder(b)
    );
    const isWeekdays = days.length === 5 && days.every(d => d >= 1 && d <= 5);
    const isWeekend = days.length === 2 && days.includes(0) && days.includes(6);
    if (isWeekdays) intervalText = 'every weekday';
    else if (isWeekend) intervalText = 'every weekend';
    else intervalText = `every ${days.map(d => SHORT_WEEKDAY[d]).join(' ')}`;
  }

  let boundText: string;
  if (bound.kind === 'duration') {
    boundText = `for ${bound.amount} ${pluralize(bound.amount, bound.unit)}`;
  } else if (bound.kind === 'times') {
    boundText = `x${bound.count}`;
  } else {
    boundText = `until ${bound.date}`;
  }

  return `${intervalText} ${boundText}`;
}

/** Sentence-cased for menu rows. */
export function describeRepeatRule(rule: RepeatRule): string {
  const text = formatRepeatRule(rule);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export type RepeatExpansion = {
  /** `YYYY-MM-DD`, ascending, never including the anchor itself. */
  dates: string[];
  /** True when the series was cut short by `limit`. */
  truncated: boolean;
};

/**
 * Expands a rule into concrete journal dates strictly after `anchor`.
 *
 * Step intervals are always computed as `anchor + k * every` rather than by
 * repeatedly adding to the previous result, so month arithmetic does not drift
 * (Jan 31 -> Feb 28 -> Mar 31, not Feb 28 -> Mar 28).
 */
export function expandRepeatRule(
  rule: RepeatRule,
  anchor: string,
  limit: number = MAX_REPEAT_OCCURRENCES
): RepeatExpansion {
  const anchorDay = dayjs(anchor, JOURNAL_DATE_FORMAT);
  if (!anchorDay.isValid()) return { dates: [], truncated: false };

  const { interval, bound } = rule;

  let endDay: dayjs.Dayjs | undefined;
  let maxCount = Infinity;
  if (bound.kind === 'duration') {
    endDay = anchorDay.add(bound.amount, bound.unit);
  } else if (bound.kind === 'until') {
    endDay = dayjs(bound.date, JOURNAL_DATE_FORMAT);
    if (!endDay.isValid()) return { dates: [], truncated: false };
  } else {
    maxCount = bound.count;
  }

  // Generate one past the limit so we can tell "exactly filled the cap" from
  // "cap cut the series short".
  const hardStop = limit + 1;
  const collected: string[] = [];

  if (interval.kind === 'step') {
    for (let k = 1; collected.length < maxCount && collected.length < hardStop; k++) {
      const day = anchorDay.add(k * interval.every, interval.unit);
      if (endDay && day.isAfter(endDay, 'day')) break;
      collected.push(day.format(JOURNAL_DATE_FORMAT));
    }
  } else {
    const wanted = new Set(interval.days);
    // Day-by-day walk, hard-bounded so a far-future end date cannot spin.
    const maxScanDays = Math.min(hardStop * 7 + 21, 366 * 6);
    for (
      let offset = 1;
      offset <= maxScanDays &&
      collected.length < maxCount &&
      collected.length < hardStop;
      offset++
    ) {
      const day = anchorDay.add(offset, 'day');
      if (endDay && day.isAfter(endDay, 'day')) break;
      if (!wanted.has(day.day())) continue;
      collected.push(day.format(JOURNAL_DATE_FORMAT));
    }
  }

  const truncated = collected.length > limit;
  return { dates: truncated ? collected.slice(0, limit) : collected, truncated };
}
